import { useState, useCallback, useEffect } from 'react';
import { navigate } from 'hooks/useHookRouterCompat';
import { useAuth } from 'lib/Auth';

interface UseUserModalReturn {
  isUserModalOpen: boolean;
  toggleUserModal: () => void;
  handleLogout: () => void;
  handleAccountClick: () => void;
  handleSettingsClick: () => void;
}

/**
 * Custom hook for managing user dropdown modal functionality
 * 
 * Handles:
 * - Modal open/close state
 * - Click outside to close functionality
 * - Navigation actions (account, settings, logout)
 * - Proper cleanup of event listeners
 * 
 * @returns User modal state and handlers
 */
export const useUserModal = (): UseUserModalReturn => {
  const { logout } = useAuth();
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  const toggleUserModal = useCallback(() => {
    setIsUserModalOpen(!isUserModalOpen);
  }, [isUserModalOpen]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/');
    setIsUserModalOpen(false);
  }, [logout]);

  const handleAccountClick = useCallback(() => {
    navigate('/account');
    setIsUserModalOpen(false);
  }, []);

  const handleSettingsClick = useCallback(() => {
    navigate('/settings');
    setIsUserModalOpen(false);
  }, []);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isUserModalOpen && !target.closest('.admin-panel')) {
        setIsUserModalOpen(false);
      }
    };

    if (isUserModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserModalOpen]);

  return {
    isUserModalOpen,
    toggleUserModal,
    handleLogout,
    handleAccountClick,
    handleSettingsClick
  };
};